/**
 * Sync Status Checker
 * Helps identify which items are synced to DynamoDB vs local-only
 */
import { DataStore } from '@aws-amplify/datastore';
import type { MutableModel } from '@aws-amplify/datastore';
import { SafetyForm } from '../models/index.js';
import { Amplify } from 'aws-amplify';
import { getCurrentUser } from '@aws-amplify/auth';

export interface SyncStatus {
    total: number;
    synced: number;
    pending: number;
    items: Array<{
        id: string;
        formType?: string;
        status?: string;
        synced: boolean;
        version?: number;
        lastChangedAt?: number;
        owner?: string;
    }>;
}

/**
 * Check sync status of SafetyForm items
 */
export async function checkSafetyFormSyncStatus(): Promise<SyncStatus> {
    try {
        const allForms = await DataStore.query(SafetyForm);

        const items = allForms.map((form: any) => {
            const version = form._version;
            const lastChangedAt = form._lastChangedAt;
            const synced = !!version && version > 0;

            return {
                id: form.id,
                formType: form.formType,
                status: form.status,
                synced,
                version: version || undefined,
                lastChangedAt: lastChangedAt || undefined,
                owner: form.owner,
            };
        });

        const synced = items.filter(item => item.synced).length;
        const pending = items.filter(item => !item.synced).length;

        return {
            total: allForms.length,
            synced,
            pending,
            items,
        };
    } catch (error: any) {
        console.error('Error checking sync status:', error);
        return {
            total: 0,
            synced: 0,
            pending: 0,
            items: [],
        };
    }
}

/**
 * Get detailed sync status with recommendations
 */
export async function getDetailedSyncStatus() {
    const status = await checkSafetyFormSyncStatus();

    console.log('📊 Sync Status Report');
    console.log('====================');
    console.log(`Total items: ${status.total}`);
    console.log(`✅ Synced to DynamoDB: ${status.synced}`);
    console.log(`⏳ Pending sync: ${status.pending}`);

    if (status.pending > 0) {
        console.log('\n⏳ Pending items (not yet synced):');

        // Get current user ID for comparison
        let currentUserId: string | null = null;
        try {
            const user = await getCurrentUser().catch(() => null);
            currentUserId = user?.username || (user as any)?.attributes?.sub || null;
        } catch (e) {
            // Not authenticated
        }

        status.items
            .filter(item => !item.synced)
            .forEach((item, index) => {
                console.log(`   ${index + 1}. ID: ${item.id.substring(0, 20)}...`);
                console.log(`      Form Type: ${item.formType || 'N/A'}`);
                console.log(`      Status: ${item.status || 'N/A'}`);
                console.log(`      Owner: ${item.owner || '❌ MISSING'}`);
                console.log(`      Current User: ${currentUserId || '❌ Not authenticated'}`);
                if (item.owner && currentUserId) {
                    console.log(`      Owner Match: ${item.owner === currentUserId ? '✅ Yes' : '❌ No'}`);
                }
                console.log(`      Reason: No version number (created offline or sync pending)`);
                if (!item.owner || (currentUserId && item.owner !== currentUserId)) {
                    console.log(`      ⚠️ CRITICAL: Owner field is missing or incorrect!`);
                    console.log(`      💡 Run: fixPendingItemsOwner() to fix this`);
                }
            });

        console.log('\n💡 Recommendations:');
        console.log('   1. Make sure you are signed in');
        console.log('   2. Check that you are online');
        console.log('   3. Wait 10-30 seconds for automatic sync');
        console.log('   4. Check browser console for sync errors');
        console.log('   5. Try refreshing the page to trigger sync');
    }

    if (status.synced > 0) {
        console.log('\n✅ Synced items:');
        status.items
            .filter(item => item.synced)
            .slice(0, 5) // Show first 5
            .forEach((item, index) => {
                console.log(`   ${index + 1}. ID: ${item.id.substring(0, 20)}...`);
                console.log(`      Version: ${item.version}`);
                console.log(`      Last Changed: ${item.lastChangedAt ? new Date(item.lastChangedAt).toISOString() : 'N/A'}`);
            });
        if (status.synced > 5) {
            console.log(`   ... and ${status.synced - 5} more`);
        }
    }

    return status;
}

/**
 * Diagnose why items aren't syncing
 */
export async function diagnoseSyncIssues() {
    console.log('🔍 Diagnosing Sync Issues');
    console.log('========================');

    const status = await checkSafetyFormSyncStatus();

    // Check authentication
    let isAuthenticated = false;
    let authDetails = '';
    try {
        const cognitoKeys = Object.keys(localStorage).filter(key =>
            key.includes('CognitoIdentityServiceProvider') ||
            key.includes('aws-amplify')
        );
        isAuthenticated = cognitoKeys.length > 0;
        if (isAuthenticated) {
            authDetails = `Found ${cognitoKeys.length} auth keys`;
        }
    } catch (e) {
        // localStorage not available
    }

    console.log('\n1. Authentication:', isAuthenticated ? '✅ Signed in' : '❌ Not signed in');
    if (authDetails) {
        console.log('   Details:', authDetails);
    }
    if (!isAuthenticated) {
        console.log('   ⚠️ CRITICAL: You must be signed in for sync to work!');
    }

    // Check network
    const isOnline = navigator.onLine;
    console.log('\n2. Network:', isOnline ? '✅ Online' : '❌ Offline');
    if (!isOnline) {
        console.log('   ⚠️ Items will sync when you come back online');
    }

    // Check Amplify config
    let hasConfig = false;
    let configDetails = '';
    try {
        const config: any = Amplify.getConfig();
        hasConfig = !!(config.API?.GraphQL);
        if (hasConfig && config.API?.GraphQL) {
            configDetails = `Endpoint: ${(config.API.GraphQL as any).endpoint.substring(0, 50)}...`;
        }
    } catch (e) {
        // Config not available
    }
    console.log('\n3. Amplify Config:', hasConfig ? '✅ Configured' : '❌ Missing');
    if (configDetails) {
        console.log('   Details:', configDetails);
    }

    // Check DataStore
    let datastoreWorking = false;
    try {
        // DataStore.query doesn't accept options object - just query the model
        const testQuery = await DataStore.query(SafetyForm);
        datastoreWorking = true;
        console.log('   Test query successful, found', testQuery.length, 'items');
    } catch (e: any) {
        console.log('   Error:', e?.message || String(e));
        console.log('   Stack:', e?.stack);
    }
    console.log('\n4. DataStore:', datastoreWorking ? '✅ Working' : '❌ Not working');

    // Check pending items in detail
    if (status.pending > 0) {
        console.log('\n5. PENDING ITEMS ANALYSIS:');
        const pendingItems = status.items.filter(item => !item.synced);
        pendingItems.forEach((item, index) => {
            console.log(`   Item ${index + 1}:`);
            console.log(`     ID: ${item.id}`);
            console.log(`     Form Type: ${item.formType || 'N/A'}`);
            console.log(`     Status: ${item.status || 'N/A'}`);
            console.log(`     Created: ${item.version ? 'Has version' : 'No version (local only)'}`);
            console.log(`     Last Changed: ${item.lastChangedAt ? new Date(item.lastChangedAt).toISOString() : 'N/A'}`);
        });
    }

    // Summary
    console.log('\n📊 Summary:');
    console.log(`   Total items: ${status.total}`);
    console.log(`   Synced: ${status.synced}`);
    console.log(`   Pending: ${status.pending}`);

    if (status.pending > 0) {
        console.log('\n🔧 Possible Reasons Items Aren\'t Syncing:');
        if (!isAuthenticated) {
            console.log('   1. ❌ Not signed in - SIGN IN to enable sync');
        }
        if (!isOnline) {
            console.log('   2. ❌ Offline - items will sync when online');
        }
        if (!hasConfig) {
            console.log('   3. ❌ Amplify not configured - check aws-exports.js');
        }
        if (!datastoreWorking) {
            console.log('   4. ❌ DataStore not working - check console for errors');
        }
        if (isAuthenticated && isOnline && hasConfig && datastoreWorking) {
            console.log('   ✅ All conditions met - items should sync automatically');
            console.log('   ⏳ DataStore syncs in batches - wait 30-60 seconds');
            console.log('   💡 Check browser console for "🔄 [SYNC EVENT]" messages');
            console.log('   💡 Try: forceSyncPendingItems() to manually trigger sync');
        }
    }

    return {
        status,
        isAuthenticated,
        isOnline,
        hasConfig,
        datastoreWorking,
    };
}

/**
 * Force sync ALL pending items (both new items and updates to existing items)
 * CRITICAL: This is called automatically after login to ensure offline items sync
 * - New items (no version): Recreated to trigger CREATE operations
 * - Updated items (have version): Force updated to trigger UPDATE operations
 * Works with allow: private schema (no owner field needed)
 */
export async function forceSyncAllPendingItems(): Promise<void> {
    console.log('🔄 Auto-syncing ALL pending items (automatic after login)...');

    try {
        const { DataStore } = await import('@aws-amplify/datastore');
        const { SafetyForm } = await import('../models/index.js');

        // Get all items
        const allItems = await DataStore.query(SafetyForm);
        console.log(`   📋 Total items: ${allItems.length}`);

        // Find items without version (new items, never synced)
        const newPendingItems = allItems.filter((item: any) => {
            return !(item._version && item._version > 0);
        });

        // Find items with version but potentially pending updates
        // Items that have been updated offline might need their updates synced
        // We'll trigger a sync by updating them
        const existingItems = allItems.filter((item: any) => {
            return !!(item._version && item._version > 0);
        });

        console.log(`   📋 New items (no version): ${newPendingItems.length}`);
        console.log(`   📋 Existing items (with version): ${existingItems.length}`);

        if (newPendingItems.length === 0 && existingItems.length === 0) {
            console.log('   ✅ No pending items to sync');
            return;
        }

        let syncedCount = 0;
        let queuedCount = 0;

        // First, handle new items (no version) - these need to be recreated
        if (newPendingItems.length > 0) {
            console.log(`   🔄 Syncing ${newPendingItems.length} new items (recreating to trigger CREATE)...`);

            for (const item of newPendingItems) {
                try {
                    const itemId = item.id.substring(0, 20);
                    console.log(`   🔄 Force syncing item ${itemId}...`);

                    // CRITICAL: Items with no version were never synced to DynamoDB
                    // DataStore.save() with copyOf() tries to UPDATE, but item doesn't exist in DynamoDB
                    // Solution: Use safetyFormService.create() which handles this correctly
                    // But we need to preserve the original ID, so we'll delete and recreate

                    try {
                        // Check if item is marked as deleted
                        const isDeleted = (item as any)._deleted;
                        if (isDeleted) {
                            console.log(`   ⏭️ Item ${itemId} is marked as deleted, skipping`);
                            continue;
                        }

                        // Save item data before deletion
                        const itemData = {
                            formType: item.formType || 'PTP',
                            projectName: item.projectName,
                            location: item.location,
                            workDate: item.workDate,
                            foremanName: item.foremanName,
                            foremanSignature: item.foremanSignature,
                            requiredPermits: item.requiredPermits || [],
                            requiredChecklist: item.requiredChecklist || [],
                            ppe: item.ppe,
                            hazards: item.hazards || [],
                            controlMeasures: item.controlMeasures || [],
                            tasks: item.tasks || [],
                            emergencyActionPlan: item.emergencyActionPlan,
                            emergencyMusterArea: item.emergencyMusterArea,
                            emergencyContacts: item.emergencyContacts,
                            teamMembers: item.teamMembers || [],
                            crewSignatures: item.crewSignatures,
                            nobodyHurtAcknowledgment: item.nobodyHurtAcknowledgment,
                            nobodyHurtDate: item.nobodyHurtDate,
                            supervisorStartSignature: item.supervisorStartSignature,
                            supervisorStartDate: item.supervisorStartDate,
                            supervisorStartPhone: item.supervisorStartPhone,
                            endOfShiftReview: item.endOfShiftReview,
                            superintendentSignature: item.superintendentSignature,
                            superintendentReviewDate: item.superintendentReviewDate,
                            superintendentEndPhone: item.superintendentEndPhone,
                            status: item.status || 'draft',
                            createdAt: item.createdAt || new Date().toISOString(),
                            updatedAt: new Date().toISOString(),
                        };

                        // Delete the old item (local only, doesn't affect DynamoDB)
                        try {
                            await DataStore.delete(item);
                            console.log(`   🗑️ Deleted local item ${itemId}`);
                        } catch (deleteError: any) {
                            console.log(`   ⚠️ Could not delete local item: ${deleteError?.message}`);
                            // Continue anyway - we'll try to create new one
                        }

                        // Wait a moment
                        await new Promise(resolve => setTimeout(resolve, 500));

                        // Create new item using safetyFormService (this will trigger CREATE in DynamoDB)
                        const { safetyFormService } = await import('../services/amplifyDataService');
                        const recreated = await safetyFormService.create(itemData);

                        console.log(`   ✅ Recreated item with new ID: ${recreated.id.substring(0, 20)}...`);

                        // Wait for sync
                        await new Promise(resolve => setTimeout(resolve, 1000));

                        // Check if it got a version
                        const reQueried = await DataStore.query(SafetyForm, recreated.id);
                        const version = (reQueried as any)?._version;

                        if (version && version > 0) {
                            console.log(`   ✅ Item synced! Version: ${version}`);
                            syncedCount++;
                        } else {
                            console.log(`   ⏳ Item created, waiting for sync...`);
                            queuedCount++;
                        }

                    } catch (error: any) {
                        console.error(`   ❌ Error recreating item ${item.id.substring(0, 20)}:`, error?.message);
                        console.error(`   Error details:`, error);
                        console.error(`   ⚠️ This item may need to be manually recreated in the UI`);
                    }
                } catch (error: any) {
                    console.error(`   ❌ Error force syncing item ${item.id.substring(0, 20)}:`, error?.message);
                    console.error(`   Error details:`, error);
                }
            }
        }

        // Second, handle existing items with versions - trigger sync for any pending updates
        // This ensures updates made offline are synced after re-login
        if (existingItems.length > 0) {
            console.log(`\n   🔄 Syncing ${existingItems.length} existing items (triggering UPDATE sync for offline edits)...`);

            for (const item of existingItems) {
                try {
                    const itemId = item.id.substring(0, 20);
                    const currentVersion = (item as any)._version;

                    // Force update to trigger sync of any pending changes
                    // This ensures updates made offline are synced after re-login
                    await DataStore.save(
                        SafetyForm.copyOf(item, (updated: MutableModel<SafetyForm>) => {
                            // Just update the timestamp to trigger sync
                            // This will sync any pending changes made offline
                            updated.updatedAt = new Date().toISOString();
                        })
                    );

                    // Wait a moment
                    await new Promise(resolve => setTimeout(resolve, 500));

                    // Check if version incremented (indicating sync)
                    const reQueried = await DataStore.query(SafetyForm, item.id);
                    const newVersion = (reQueried as any)?._version;

                    if (newVersion && newVersion > currentVersion) {
                        console.log(`   ✅ Item ${itemId} update synced (version ${currentVersion} → ${newVersion})`);
                        syncedCount++;
                    } else {
                        console.log(`   ⏳ Item ${itemId} update queued (version ${currentVersion})`);
                        queuedCount++;
                    }
                } catch (error: any) {
                    console.error(`   ❌ Error syncing update for item ${item.id.substring(0, 20)}:`, error?.message);
                }
            }
        }

        console.log(`\n   ✅ Force sync completed:`);
        console.log(`      - New items processed: ${newPendingItems.length}`);
        console.log(`      - Existing items processed: ${existingItems.length}`);
        console.log(`      - Total synced: ${syncedCount} items`);
        console.log(`      - Total queued: ${queuedCount} items (will sync within 20-30 seconds)`);
        console.log('   💡 Wait 20-30 seconds and check: checkSyncStatus()');
        console.log('   💡 Check DynamoDB to verify items are synced');

    } catch (error: any) {
        console.error('❌ Error force syncing items:', error?.message || error);
    }
}

/**
 * Force sync items that have correct owner but aren't syncing
 * This is a workaround for items that should sync but don't
 */
export async function forceSyncItemsWithCorrectOwner(): Promise<void> {
    console.log('🔄 Force syncing items with correct owner...');

    try {
        const { DataStore } = await import('@aws-amplify/datastore');
        const { SafetyForm } = await import('../models/index.js');
        const { safetyFormService } = await import('../services/amplifyDataService');

        // Get current user
            const user = await getCurrentUser().catch(() => null);
            const currentUserId = user?.username || (user as any)?.attributes?.sub;

        if (!currentUserId) {
            console.error('❌ Cannot force sync - user not authenticated');
            return;
        }

        console.log('   👤 Current user:', currentUserId);

        // Get all items
        const allItems = await DataStore.query(SafetyForm);

        // Find items with correct owner but no version
        const itemsToSync = allItems.filter((item: any) => {
            const hasVersion = !!(item._version && item._version > 0);
            const hasCorrectOwner = (item.owner === currentUserId);
            return !hasVersion && hasCorrectOwner;
        });

        if (itemsToSync.length === 0) {
            console.log('   ✅ No items need force sync');
            return;
        }

        console.log(`   📋 Found ${itemsToSync.length} items with correct owner that need sync`);
        console.log('   🔄 Force updating items to trigger sync...');

        let syncedCount = 0;

        for (const item of itemsToSync) {
            try {
                const itemId = item.id.substring(0, 20);
                console.log(`   🔄 Force syncing item ${itemId}...`);

                // Force update by modifying a field
                // This should trigger DataStore to sync
                await safetyFormService.update(item.id, {
                    updatedAt: new Date().toISOString(),
                });

                // Wait a moment
                await new Promise(resolve => setTimeout(resolve, 500));

                // Re-query to check if version was assigned
                const reQueried = await DataStore.query(SafetyForm, item.id);
                const version = (reQueried as any)?._version;

                if (version && version > 0) {
                    console.log(`   ✅ Item ${itemId} now syncing (version ${version})`);
                    syncedCount++;
                } else {
                    console.log(`   ⏳ Item ${itemId} update queued, waiting for sync...`);
                }
            } catch (error: any) {
                console.error(`   ❌ Error force syncing item ${item.id.substring(0, 20)}:`, error?.message);
            }
        }

        console.log(`\n   ✅ Force sync completed:`);
        console.log(`      - Processed: ${itemsToSync.length} items`);
        console.log(`      - Synced: ${syncedCount} items`);
        console.log(`      - Pending: ${itemsToSync.length - syncedCount} items`);
        console.log('   💡 Wait 20-30 seconds and check: checkSyncStatus()');

    } catch (error: any) {
        console.error('❌ Error force syncing items:', error?.message || error);
    }
}

/**
 * Force sync pending items by updating them
 * This can help trigger sync for items that are stuck
 */
export async function forceSyncPendingItems(): Promise<void> {
    console.log('🔄 Attempting to force sync pending items...');

    try {
        const status = await checkSafetyFormSyncStatus();
        const pendingItems = status.items.filter(item => !item.synced);

        if (pendingItems.length === 0) {
            console.log('✅ No pending items to sync');
            return;
        }

        console.log(`📋 Found ${pendingItems.length} pending items`);

        // Try to trigger sync by querying and then updating each item
        const { safetyFormService } = await import('../services/amplifyDataService');

        for (const item of pendingItems) {
            try {
                console.log(`\n🔄 Processing item: ${item.id.substring(0, 20)}...`);

                // Get the full item
                const fullItem = await safetyFormService.getById(item.id);

                if (fullItem) {
                    // Update the item with a minor change to trigger sync
                    // This is a workaround - DataStore should sync automatically
                    const currentStatus = (fullItem as any).status;
                    if (typeof currentStatus === 'string') {
                        const updated = await safetyFormService.update(item.id, {
                            status: currentStatus,
                        });

                        const version = (updated as any)._version;
                        if (version) {
                            console.log(`   ✅ Item now has version ${version} - sync triggered!`);
                        } else {
                            console.log(`   ⏳ Item still pending - sync may be queued`);
                        }
                    } else {
                        // If no status, just update updatedAt to trigger sync
                        const updated = await safetyFormService.update(item.id, {
                            updatedAt: new Date().toISOString(),
                        });

                        const version = (updated as any)._version;
                        if (version) {
                            console.log(`   ✅ Item now has version ${version} - sync triggered!`);
                        } else {
                            console.log(`   ⏳ Item still pending - sync may be queued`);
                        }
                    }
                }
            } catch (error: any) {
                console.error(`   ❌ Error syncing item ${item.id}:`, error?.message || error);
            }
        }

        console.log('\n✅ Force sync attempt completed');
        console.log('💡 Check sync status again in 10-20 seconds: checkSyncStatus()');
        console.log('💡 If items still don\'t sync, try: restartDataStore()');

    } catch (error: any) {
        console.error('❌ Error in forceSyncPendingItems:', error?.message || error);
    }
}

/**
 * Restart DataStore to trigger sync
 * Sometimes DataStore needs to be restarted to pick up authentication changes
 */
export async function restartDataStore(): Promise<void> {
    console.log('🔄 Restarting DataStore...');

    try {
        const { DataStore } = await import('@aws-amplify/datastore');
        const { AmplifyDataService } = await import('../services/amplifyDataService');

        // Stop DataStore
        console.log('1. Stopping DataStore...');
        try {
            await DataStore.stop();
            console.log('   ✅ DataStore stopped');
        } catch (error: any) {
            console.warn('   ⚠️ Error stopping DataStore (may not be running):', error?.message);
        }

        // Wait a moment
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Start DataStore again
        console.log('2. Starting DataStore...');
        const started = await AmplifyDataService.start();

        if (started) {
            console.log('   ✅ DataStore restarted successfully');
            console.log('   💡 DataStore will now attempt to sync pending items');
            console.log('   💡 Wait 20-30 seconds and check: checkSyncStatus()');
        } else {
            console.error('   ❌ Failed to restart DataStore');
            console.error('   💡 Check console for errors');
        }

    } catch (error: any) {
        console.error('❌ Error restarting DataStore:', error?.message || error);
        console.error('   Stack:', error?.stack);
    }
}

/**
 * Fix owner fields for items created in previous sessions
 * This is called automatically after login, but can be run manually
 */
export async function fixPendingItemsOwner(): Promise<void> {
    console.log('🔧 Fixing owner fields for pending items...');

    try {
        const user = await getCurrentUser().catch(() => null);
        const currentUserId = user?.username || (user as any)?.attributes?.sub;

        if (!currentUserId) {
            console.error('❌ Cannot get current user ID');
            return;
        }

        console.log('   Current user ID:', currentUserId);

        const { SafetyForm } = await import('../models/index.js');
        const { safetyFormService } = await import('../services/amplifyDataService');

        // Get all SafetyForms
        const allForms = await DataStore.query(SafetyForm);

        // Find items without version (not synced)
        const pendingItems = allForms.filter((form: any) => {
            return !(form._version && form._version > 0);
        });

        if (pendingItems.length === 0) {
            console.log('   ✅ No pending items found');
            return;
        }

        console.log(`   📋 Found ${pendingItems.length} pending items`);

        // Update each item to trigger sync with correct owner
        let fixed = 0;
        for (const item of pendingItems) {
            try {
                // Update the item - DataStore will set the owner field automatically
                const updated = await safetyFormService.update(item.id, {
                    updatedAt: new Date().toISOString(),
                });

                const version = (updated as any)._version;
                if (version) {
                    console.log(`   ✅ Item ${item.id.substring(0, 20)}... now syncing (version ${version})`);
                    fixed++;
                } else {
                    console.log(`   ⏳ Item ${item.id.substring(0, 20)}... owner fixed, sync queued`);
                }
            } catch (error: any) {
                console.error(`   ❌ Error fixing item ${item.id}:`, error?.message);
            }
        }

        console.log(`\n✅ Fixed ${fixed} items`);
        console.log('💡 Items should sync within 20-30 seconds');
        console.log('💡 Check status: checkSyncStatus()');

    } catch (error: any) {
        console.error('❌ Error fixing owner fields:', error?.message || error);
    }
}

/**
 * Check if DataStore sync is actually enabled
 * Verifies that DataStore can sync to cloud
 */
export async function checkDataStoreSyncEnabled(): Promise<boolean> {
    console.log('🔍 Checking if DataStore sync is enabled...');

    try {
        // Check Amplify config
        const aws: any = Amplify.getConfig();

        if (!aws || !aws.API?.GraphQL) {
            console.log('   ❌ GraphQL endpoint not configured');
            return false;
        }
        console.log('   ✅ GraphQL endpoint configured');
        console.log('   Endpoint:', (aws.API.GraphQL as any).endpoint);

        // Check authentication using Auth API
        let isAuthenticated = false;
        let userId = null;
        try {
            const user = await getCurrentUser().catch(() => null);
            isAuthenticated = !!user;
            userId = user?.username || (user as any)?.attributes?.sub || null;
            console.log('   ✅ User authenticated via Auth API');
            console.log('   User ID:', userId);
        } catch (authError: any) {
            console.log('   ❌ Not authenticated via Auth API:', authError?.message);
            // Fallback: check localStorage
            const cognitoKeys = Object.keys(localStorage).filter(key =>
                key.includes('CognitoIdentityServiceProvider') ||
                key.includes('aws-amplify')
            );
            isAuthenticated = cognitoKeys.length > 0;
            if (isAuthenticated) {
                console.log('   ⚠️ Auth tokens found but retrieving current user failed');
                console.log('   This might indicate an auth issue');
            }
        }

        if (!isAuthenticated) {
            console.log('   ❌ Not authenticated - sync disabled');
            console.log('   💡 Sign in to enable sync');
            return false;
        }

        // Check network
        if (!navigator.onLine) {
            console.log('   ⚠️ Offline - sync will happen when online');
            return false;
        }
        console.log('   ✅ Online');

        // Check if DataStore is started
        try {
            const { DataStore } = await import('@aws-amplify/datastore');
            const { SafetyForm } = await import('../models/index.js');
            const testQuery = await DataStore.query(SafetyForm);
            console.log('   ✅ DataStore is running');
            console.log('   Local items:', testQuery.length);

            // Check if any items have versions (indicating sync is working)
            const syncedItems = testQuery.filter((item: any) => item._version && item._version > 0);
            console.log('   Synced items:', syncedItems.length);

            if (syncedItems.length === 0 && testQuery.length > 0) {
                console.log('   ⚠️ WARNING: No items have versions - sync may not be working!');
                console.log('   💡 This suggests DataStore is in local-only mode');
            }
        } catch (error: any) {
            console.log('   ❌ DataStore not working:', error?.message);
            return false;
        }

        // Check for sync warnings in console
        console.log('\n📋 Sync Status Check:');
        console.log('   Check browser console for these warnings:');
        console.log('   - "DataStore - Data won\'t be synchronized"');
        console.log('   - "No GraphQL endpoint configured"');
        console.log('   - Any red error messages about sync');

        console.log('\n✅ All checks passed - DataStore sync should be enabled');
        console.log('💡 If items still don\'t sync, try:');
        console.log('   1. restartDataStore() - restart DataStore');
        console.log('   2. testSyncWithNewItem() - test if NEW items sync');
        console.log('   3. If new items sync but old ones don\'t, clear IndexedDB');

        return true;

    } catch (error: any) {
        console.error('❌ Error checking DataStore sync:', error?.message || error);
        return false;
    }
}

/**
 * Test if sync is working by creating a new item
 * This helps determine if the issue is with old items or sync in general
 */
export async function testSyncWithNewItem(): Promise<void> {
    console.log('🧪 Testing sync with a new item...');

    try {
        const { safetyFormService } = await import('../services/amplifyDataService');

        // Create a test item
        console.log('1. Creating test PTP...');
        const testItem = await safetyFormService.create({
            formType: 'TEST_SYNC',
            status: 'draft',
            projectName: 'Sync Test',
        });

        console.log('   ✅ Test item created:', testItem.id);

        const version = (testItem as any)._version;
        if (version) {
            console.log('   ✅ Item has version immediately - sync is working!');
            console.log('   Version:', version);
        } else {
            console.log('   ⏳ Item has no version yet - waiting for sync...');
            console.log('   💡 Check again in 20-30 seconds');
        }

        // Wait a bit and check again
        console.log('\n2. Waiting 5 seconds and checking again...');
        await new Promise(resolve => setTimeout(resolve, 5000));

        const updatedItem = await safetyFormService.getById(testItem.id);
        const updatedVersion = (updatedItem as any)?._version;

        if (updatedVersion) {
            console.log('   ✅ Item now has version - sync is working!');
            console.log('   Version:', updatedVersion);
            console.log('\n✅ CONCLUSION: Sync is working for NEW items');
            console.log('💡 Old items that don\'t sync were likely created before authentication');
            console.log('💡 Solution: Clear IndexedDB to remove old items, keep synced ones in DynamoDB');
        } else {
            console.log('   ❌ Item still has no version - sync is NOT working');
            console.log('\n❌ CONCLUSION: Sync is not working');
            console.log('💡 Run: diagnoseSync() to find the issue');
            console.log('💡 Run: checkDataStoreSyncEnabled() to verify configuration');
        }

        // Ask user if they want to keep the test item
        console.log('\n3. Test item cleanup:');
        console.log('   Test item ID:', testItem.id);
        console.log('   Form Type:', (testItem as any).formType);
        console.log('   💡 The test item is kept for you to verify');
        console.log('   💡 You can delete it manually from the dashboard if needed');
        console.log('   💡 Or run: safetyFormService.delete("' + testItem.id + '")');

        // Don't auto-delete - let user verify it appears in dashboard
        // The dashboard should pick it up via DataStore.observe()

    } catch (error: any) {
        console.error('❌ Error testing sync:', error?.message || error);
    }
}

/**
 * Check owner field status for all pending items
 * Helps diagnose why items aren't syncing
 */
export async function checkItemOwnerStatus(): Promise<void> {
    console.log('🔍 Checking owner field status for all items...');

    try {
        const { DataStore } = await import('@aws-amplify/datastore');
        const { SafetyForm } = await import('../models/index.js');

        // Get current user
        let currentUserId: string | null = null;
        try {
            const user = await getCurrentUser().catch(() => null);
            currentUserId = user?.username || (user as any)?.attributes?.sub || null;
            console.log('   👤 Current user ID:', currentUserId || '❌ Not authenticated');
        } catch (e) {
            console.error('   ❌ Cannot get current user:', e);
            return;
        }

        if (!currentUserId) {
            console.error('   ❌ Cannot check owner status - user not authenticated');
            return;
        }

        // Get all items
        const allItems = await DataStore.query(SafetyForm);
        console.log(`   📋 Total items: ${allItems.length}`);

        // Check each item
        const itemsNeedingFix: any[] = [];
        const itemsWithCorrectOwner: any[] = [];

        allItems.forEach((item: any) => {
            const hasVersion = !!(item._version && item._version > 0);
            const hasOwner = !!(item.owner);
            const correctOwner = (item.owner === currentUserId);

            if (!hasVersion) {
                // Item not synced
                if (!hasOwner || !correctOwner) {
                    itemsNeedingFix.push({
                        id: item.id,
                        formType: item.formType,
                        owner: item.owner || 'MISSING',
                        version: item._version || 0,
                        needsFix: true,
                    });
                } else {
                    itemsWithCorrectOwner.push({
                        id: item.id,
                        formType: item.formType,
                        owner: item.owner,
                        version: item._version || 0,
                        needsFix: false,
                    });
                }
            }
        });

        console.log(`\n   📊 Summary:`);
        console.log(`      Items needing owner fix: ${itemsNeedingFix.length}`);
        console.log(`      Items with correct owner (pending sync): ${itemsWithCorrectOwner.length}`);

        if (itemsNeedingFix.length > 0) {
            console.log(`\n   ⚠️ Items that need owner fix:`);
            itemsNeedingFix.forEach((item, index) => {
                console.log(`      ${index + 1}. ${item.id.substring(0, 20)}...`);
                console.log(`         Owner: ${item.owner}`);
                console.log(`         Expected: ${currentUserId}`);
                console.log(`         Version: ${item.version}`);
            });
            console.log(`\n   💡 Run: fixPendingItemsOwner() to fix these items`);
        }

        if (itemsWithCorrectOwner.length > 0) {
            console.log(`\n   ✅ Items with correct owner (should sync soon):`);
            itemsWithCorrectOwner.slice(0, 5).forEach((item, index) => {
                console.log(`      ${index + 1}. ${item.id.substring(0, 20)}... (owner: ${item.owner})`);
            });
            if (itemsWithCorrectOwner.length > 5) {
                console.log(`      ... and ${itemsWithCorrectOwner.length - 5} more`);
            }
        }

    } catch (error: any) {
        console.error('   ❌ Error checking owner status:', error?.message || error);
    }
}

/**
 * Check if a specific item exists in DataStore
 * Useful for debugging why items don't appear in dashboard
 */
export async function checkItemInDataStore(itemId: string): Promise<void> {
    console.log(`🔍 Checking if item exists in DataStore: ${itemId.substring(0, 20)}...`);

    try {
        const { DataStore } = await import('@aws-amplify/datastore');
        const { SafetyForm } = await import('../models/index.js');

        // Try to query by ID
        const allItems = await DataStore.query(SafetyForm);
        const foundItem = allItems.find((item: any) => item.id === itemId);

        if (foundItem) {
            console.log('   ✅ Item found in DataStore!');
            console.log('   ID:', foundItem.id);
            console.log('   Form Type:', foundItem.formType);
            console.log('   Status:', foundItem.status);
            console.log('   Version:', (foundItem as any)._version || 'N/A (not synced)');
            console.log('   Last Changed:', (foundItem as any)._lastChangedAt || 'N/A');
            console.log('   Owner:', (foundItem as any).owner || 'N/A');
            console.log('\n   💡 If this item is not showing in dashboard:');
            console.log('      1. Check if dashboard is filtering it out (search/filter)');
            console.log('      2. Try refreshing the dashboard page');
            console.log('      3. Check browser console for errors');
            console.log('      4. Run: checkSyncStatus() to see sync state');
        } else {
            console.log('   ❌ Item NOT found in DataStore');
            console.log('   Total items in DataStore:', allItems.length);
            console.log('\n   💡 Possible reasons:');
            console.log('      1. Item was deleted');
            console.log('      2. Item was never created');
            console.log('      3. Item is in a different database/table');
            console.log('      4. DataStore query is not working');
        }

        // Also check if it's in the list
        console.log('\n   📋 All items in DataStore:');
        allItems.slice(0, 10).forEach((item: any, index: number) => {
            console.log(`      ${index + 1}. ${item.id.substring(0, 20)}... - ${item.formType || 'N/A'}`);
        });
        if (allItems.length > 10) {
            console.log(`      ... and ${allItems.length - 10} more`);
        }

    } catch (error: any) {
        console.error('   ❌ Error checking item:', error?.message || error);
    }
}

/**
 * Clear IndexedDB (useful for testing/fixing sync issues)
 * ⚠️ WARNING: This will delete ALL local data!
 */
export async function clearIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB not available'));
            return;
        }

        indexedDB.databases().then(databases => {
            const deletePromises = databases.map(db => {
                return new Promise<void>((resolve, reject) => {
                    if (!db.name) {
                        resolve();
                        return;
                    }
                    const deleteRequest = indexedDB.deleteDatabase(db.name);
                    deleteRequest.onsuccess = () => {
                        console.log(`✅ Deleted database: ${db.name}`);
                        resolve();
                    };
                    deleteRequest.onerror = () => {
                        console.error(`❌ Failed to delete database: ${db.name}`);
                        reject(deleteRequest.error);
                    };
                    deleteRequest.onblocked = () => {
                        console.warn(`⚠️ Database ${db.name} is blocked (close all tabs)`);
                        resolve(); // Continue anyway
                    };
                });
            });

            Promise.all(deletePromises)
                .then(() => {
                    console.log('✅ All IndexedDB databases cleared');
                    console.log('💡 Refresh the page to see changes');
                    resolve();
                })
                .catch(reject);
        }).catch(reject);
    });
}

// Expose globally for debugging
if (typeof window !== 'undefined') {
    (window as any).checkSyncStatus = getDetailedSyncStatus;
    (window as any).diagnoseSync = diagnoseSyncIssues;
    (window as any).forceSyncPendingItems = forceSyncPendingItems;
    (window as any).forceSyncAllPendingItems = forceSyncAllPendingItems;
    (window as any).forceSyncItemsWithCorrectOwner = forceSyncItemsWithCorrectOwner;
    (window as any).fixPendingItemsOwner = fixPendingItemsOwner;
    (window as any).restartDataStore = restartDataStore;
    (window as any).checkDataStoreSyncEnabled = checkDataStoreSyncEnabled;
    (window as any).testSyncWithNewItem = testSyncWithNewItem;
    (window as any).checkItemInDataStore = checkItemInDataStore;
    (window as any).checkItemOwnerStatus = checkItemOwnerStatus;
    (window as any).clearIndexedDB = clearIndexedDB;
    console.log('💡 Tips:');
    console.log('   - Run checkSyncStatus() to see sync status');
    console.log('   - Run diagnoseSync() to diagnose why items aren\'t syncing');
    console.log('   - Run checkDataStoreSyncEnabled() to verify sync is enabled');
    console.log('   - Run testSyncWithNewItem() to test if sync works for NEW items');
    console.log('   - Run checkItemInDataStore("item-id") to check if an item exists');
    console.log('   - Run fixPendingItemsOwner() to fix owner fields for items from previous sessions');
    console.log('   - Run forceSyncPendingItems() to manually trigger sync for pending items');
    console.log('   - Run restartDataStore() to restart DataStore and trigger sync');
    console.log('   - Run clearIndexedDB() to clear all local IndexedDB data (⚠️ destructive!)');
}
